import {MutableRefObject, useCallback, useRef} from 'react';
import * as THREE from 'three';
import {useAppDispatch} from 'store/customer';
import {setHasWebGLError} from 'components/customer/Preview3DCommon/store/viewerSlice';
import {attachWebGLContextLostHandler} from 'components/customer/Preview3DCommon/lib/attachWebGLContextLostHandler';
import {isWebGLRelatedError} from 'components/customer/Preview3DCommon/lib/isWebGLRelatedError';

type MountRef = MutableRefObject<HTMLDivElement | null | undefined>;

export type RendererSetupOptions = {
    mountRef?: MountRef;
    appendToMount?: boolean;
    rendererParams?: THREE.WebGLRendererParameters;
    pixelRatio?: number;
    clearColor?: THREE.ColorRepresentation;
    clearAlpha?: number;
    autoClear?: boolean;
    domElementStyle?: Partial<CSSStyleDeclaration>;
};

const useRendererSetup = (options?: RendererSetupOptions) => {
    const renderer = useRef<THREE.WebGLRenderer>();
    const unsubscribeContextLost = useRef<(() => void) | null>(null);
    const dispatch = useAppDispatch();

    const setupRenderer = useCallback(
        (
            mountRefArg?: MountRef,
            override?: Pick<RendererSetupOptions, 'appendToMount'>
        ) => {
            const mountRef = mountRefArg ?? options?.mountRef;
            const mountEl = mountRef?.current;

            if (!mountEl) {
                throw new Error(
                    'useRendererSetup: mountRef.current is not available'
                );
            }

            const dimension = mountEl.getBoundingClientRect();

            try {
                renderer.current = new THREE.WebGLRenderer({
                    antialias: true,
                    // Faded panel outlines use the stencil buffer so
                    // coincident ones don't stack into dark lines.
                    stencil: true,
                    ...(options?.rendererParams ?? {}),
                });
            } catch (error) {
                if (error instanceof Error && isWebGLRelatedError(error)) {
                    dispatch(setHasWebGLError(true));
                    return renderer;
                }
                throw error;
            }

            // Guards against attaching a second listener
            unsubscribeContextLost.current?.();
            unsubscribeContextLost.current = attachWebGLContextLostHandler(
                renderer.current.domElement,
                () => {
                    // eslint-disable-next-line no-console -- No logger available in this context, console.error is acceptable
                    console.error(
                        'WebGL context lost (browser will attempt to restore it automatically)'
                    );
                }
            );

            const pixelRatio =
                typeof options?.pixelRatio === 'number'
                    ? options.pixelRatio
                    : typeof window !== 'undefined'
                      ? window.devicePixelRatio
                      : 1;

            renderer.current.setPixelRatio(pixelRatio);
            renderer.current.setSize(dimension.width, dimension.height);

            if (typeof options?.autoClear === 'boolean') {
                renderer.current.autoClear = options.autoClear;
            } else {
                renderer.current.autoClear = false;
            }

            if (typeof options?.clearColor !== 'undefined') {
                renderer.current.setClearColor(
                    options.clearColor,
                    options.clearAlpha ?? 1
                );
            }

            if (options?.domElementStyle) {
                Object.assign(renderer.current.domElement.style, {
                    ...(options.domElementStyle ?? {}),
                });
            }

            const appendToMount =
                override?.appendToMount ?? options?.appendToMount ?? true;
            if (
                appendToMount &&
                renderer.current.domElement.parentElement !== mountEl
            ) {
                mountEl.appendChild(renderer.current.domElement);
            }

            return renderer;
        },
        [
            dispatch,
            options?.appendToMount,
            options?.autoClear,
            options?.clearAlpha,
            options?.clearColor,
            options?.domElementStyle,
            options?.mountRef,
            options?.pixelRatio,
            options?.rendererParams,
        ]
    );

    return {
        setupRenderer,
        renderer,
    };
};

export default useRendererSetup;
